import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToSection } from "@/App";

const EASE = [0.76, 0, 0.24, 1];

const FadeUp = ({ children, delay, className = "" }) => (
  <motion.div
    initial={{ y: 34 }}
    animate={{ y: 0 }}
    transition={{ duration: 1, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const DUST = [
  { left: 50, size: 2.5, dur: 21, delay: -3, drift: 18 },
  { left: 55, size: 2, dur: 26, delay: -12, drift: -14 },
  { left: 60, size: 3, dur: 24, delay: -7, drift: 26 },
  { left: 64, size: 2, dur: 29, delay: -19, drift: -20 },
  { left: 57, size: 2.5, dur: 22, delay: -15, drift: 10 },
  { left: 68, size: 2, dur: 27, delay: -5, drift: 30 },
  { left: 52, size: 1.5, dur: 30, delay: -24, drift: -8 },
  { left: 62, size: 2, dur: 23, delay: -10, drift: 16 },
  { left: 71, size: 2.5, dur: 25, delay: -17, drift: 34 },
  { left: 47, size: 1.5, dur: 28, delay: -21, drift: -12 },
  { left: 66, size: 1.5, dur: 20, delay: -2, drift: 22 },
  { left: 58, size: 2, dur: 27, delay: -26, drift: -18 },
  { left: 73, size: 2, dur: 24, delay: -13, drift: 28 },
  { left: 53, size: 2, dur: 29, delay: -8, drift: 6 },
];

const DustField = () => (
  <div
    className="dust-field absolute inset-x-[15%] top-[22%] bottom-[30%] pointer-events-none"
    aria-hidden="true"
    data-testid="hero-dust"
  >
    {DUST.map((p, i) => (
      <span
        key={i}
        style={{
          left: `${p.left}%`,
          width: p.size,
          height: p.size,
          animationDuration: `${p.dur}s`,
          animationDelay: `${p.delay}s`,
          "--dust-x": `${p.drift}px`,
        }}
      />
    ))}
  </div>
);

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen flex flex-col items-center justify-center py-24 overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div
        initial={{ scale: 0.97 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.2, delay: 0.1, ease: EASE }}
        className="absolute inset-0 flex items-center justify-center"
        aria-hidden="true"
        data-testid="hero-sculpture"
      >
        <div className="w-full h-full scale-[0.72] md:scale-100">
          <motion.img
            src="/hero-prism-clear.webp"
            alt=""
            fetchPriority="high"
            animate={{ y: [0, -14, 0], rotate: [0, 0.5, 0, -0.5, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            className="w-full h-full object-cover mix-blend-screen object-[42%_38%] md:object-center"
          />
        </div>
      </motion.div>

      <DustField />

      <div className="relative z-10 px-6 text-center max-w-4xl mx-auto">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[110%] bg-black/60 blur-[70px] rounded-full -z-10"
          aria-hidden="true"
        />
        <FadeUp delay={0.1}>
          <p className="text-xs md:text-sm tracking-[0.35em] uppercase text-signal mb-6">
            Exceptional Websites & Apps, Built for Your Brand
          </p>
        </FadeUp>

        <FadeUp delay={0.25}>
          <h1
            className="font-logo font-bold tracking-tight leading-[0.95] text-6xl sm:text-7xl lg:text-9xl"
            data-testid="hero-line-1"
          >
            <span className="text-signal">L</span>Creative{" "}
            <span className="text-outline">Studio</span>
          </h1>
        </FadeUp>

        <FadeUp delay={0.45}>
          <p
            className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto"
            data-testid="hero-description"
          >
            We combine thoughtful design with powerful technology to build
            websites and mobile apps that are tailored to each business and
            designed to grow with it. From concept and design through to
            development and launch, we provide a personalised approach focused
            on creating digital products that look exceptional and deliver real
            business value.
          </p>
        </FadeUp>

        <FadeUp delay={0.65}>
          <button
            onClick={() => scrollToSection("#contact")}
            data-testid="hero-cta"
            className="mt-8 bg-signal text-black font-display font-semibold text-sm tracking-[0.25em] uppercase px-12 py-5 hover:bg-foreground hover:text-background transition-colors duration-300"
          >
            Start Your Project
          </button>
        </FadeUp>
      </div>
      <motion.button
        initial={{ y: 12 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
        onClick={() => scrollToSection("#work")}
        data-testid="hero-scroll-indicator"
        className="hidden md:flex absolute right-12 bottom-10 z-10 items-center gap-2.5 text-xs tracking-[0.3em] uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
      >
        <motion.span
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex"
        >
          <ChevronDown size={15} strokeWidth={1.5} />
        </motion.span>
        Scroll to explore
      </motion.button>
    </section>
  );
}
